// apps/admin/src/pages/auth/Verify2FAPage.tsx
//
// Phase 1 Wave 9 Task 45 — Admin 2FA verification page.
// Same UX as merchant Verify2FAPage; admin portal scope (recovery hint
// adjusted — admins use Superadmin recovery, not self-service email).

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  AuthSplitLayout,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  useAuth,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import { setSessionHint } from '../../lib/sessionHint.js';

// Phase 8.x debug B1 — pre-auth locale switcher.
import { AdminLanguageSelector } from '../../components/shell/AdminLanguageSelector.js';
import { useTranslation } from '../../i18n/useTranslation.js';

const STORAGE_KEY = 'pw_2fa_challenge';

function readChallengeToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearChallengeToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
}

interface DigitBoxesProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  ariaInvalid?: boolean;
}

function DigitBoxes({
  value,
  onChange,
  disabled,
  ariaInvalid,
}: DigitBoxesProps): JSX.Element {
  const { t } = useTranslation('ops');
  const handleChange = (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      const arr = value.split('');
      arr[i] = '';
      onChange(arr.join('').slice(0, 6));
      return;
    }
    const arr = value.split('');
    const remaining = raw.split('').slice(0, 6 - i);
    for (let k = 0; k < remaining.length; k++) {
      arr[i + k] = remaining[k] ?? '';
    }
    const joined = arr.join('').slice(0, 6);
    onChange(joined);
    const nextIndex = Math.min(i + remaining.length, 5);
    const nextEl = document.getElementById(`otp-box-${nextIndex}`) as HTMLInputElement | null;
    nextEl?.focus();
  };

  const handleKey = (i: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) {
      const prev = document.getElementById(`otp-box-${i - 1}`) as HTMLInputElement | null;
      prev?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      const prev = document.getElementById(`otp-box-${i - 1}`) as HTMLInputElement | null;
      prev?.focus();
    }
    if (e.key === 'ArrowRight' && i < 5) {
      const nxt = document.getElementById(`otp-box-${i + 1}`) as HTMLInputElement | null;
      nxt?.focus();
    }
  };

  return (
    <div className="flex justify-between gap-2" role="group" aria-label={t('auth.verify2fa.codeAria')}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          id={`otp-box-${i}`}
          data-testid={`otp-box-${i}`}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={value[i] ?? ''}
          onChange={handleChange(i)}
          onKeyDown={handleKey(i)}
          disabled={disabled}
          aria-invalid={ariaInvalid ? 'true' : 'false'}
          className="h-12 w-12 rounded-md border border-pw-border bg-pw-bg text-center text-lg font-semibold text-pw-text placeholder:text-pw-text-muted/70 focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20 disabled:opacity-50"
        />
      ))}
    </div>
  );
}

export default function Verify2FAPage(): JSX.Element {
  const { t } = useTranslation('ops');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fetchMe = useAuth((s) => s.fetchMe);

  const methodRaw = (params.get('method') ?? 'totp').toLowerCase();
  const method: 'totp' | 'email' = methodRaw === 'email' ? 'email' : 'totp';

  // G-21 — post-verify redirect target forwarded by LoginPage (?from=).
  // Same-origin in-app paths only (single leading `/`) — no open redirect.
  const fromParam = params.get('from');
  const returnTo =
    fromParam && fromParam.startsWith('/') && !fromParam.startsWith('//')
      ? fromParam
      : '/';

  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError(t('auth.verify2fa.errors.incomplete'));
      return;
    }
    const challengeToken = readChallengeToken();
    if (!challengeToken) {
      setError(t('auth.verify2fa.errors.sessionExpired'));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const client = apiClient();
      const { response } = await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown },
          ) => Promise<{ response: Response }>;
        }
      ).POST('/api/v1/auth/verify-2fa', {
        body: {
          challengeToken,
          code,
          rememberDevice,
        },
      });
      if (!response.ok) {
        if (response.status === 410) {
          setError(t('auth.verify2fa.errors.expired'));
        } else if (response.status === 423 || response.status === 429) {
          // BE khoá do quá nhiều lần thử trả 423 (AUTH_2FA_LOCKED); 429 giữ lại
          // phòng path rate-limit khác. Cả hai → thông báo "Quá nhiều lần thử".
          setError(t('auth.verify2fa.errors.tooMany'));
        } else {
          setError(t('auth.verify2fa.errors.invalid'));
        }
        return;
      }
      clearChallengeToken();
      setSessionHint();
      await fetchMe();
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'VERIFY_2FA_FAILED');
    } finally {
      setSubmitting(false);
    }
  };

  const headline =
    method === 'email'
      ? t('auth.verify2fa.headlineEmail')
      : t('auth.verify2fa.headlineTotp');

  return (
    <AuthSplitLayout variant="verification">
      <div className="flex justify-end">
        <AdminLanguageSelector />
      </div>
      <div className="flex flex-1 items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('auth.verify2fa.title')}</CardTitle>
          <CardDescription>{headline}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={submit} noValidate>
            <FormField>
              <FormLabel>{t('auth.verify2fa.codeLabel')}</FormLabel>
              <DigitBoxes
                value={code}
                onChange={setCode}
                disabled={submitting}
                ariaInvalid={!!error}
              />
              {error ? <FormMessage>{error}</FormMessage> : null}
            </FormField>

            <label className="flex items-center gap-2 text-sm text-pw-text-muted">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-pw-border"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />
              {t('auth.verify2fa.trustDevice')}
            </label>

            <Button
              type="submit"
              variant="default"
              size="default"
              className="w-full"
              disabled={submitting || code.length !== 6}
              data-testid="verify-2fa-submit"
            >
              {submitting ? t('auth.verify2fa.submitting') : t('auth.verify2fa.submit')}
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="default"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              {t('auth.verify2fa.backToLogin')}
            </Button>

            <p className="mt-2 text-center text-xs text-pw-text-muted">
              {t('auth.verify2fa.recoveryHint')}
            </p>
          </Form>
        </CardContent>
      </Card>
    </div>
    </AuthSplitLayout>
  );
}
